import FeatureGates from '@atlaskit/feature-gate-js-client';
import {
	type ActiveScopeEntry,
	type DismissCallback,
	type PostOfficeContextValue,
	type RouteMatch,
	type RouteState,
	createMatchScopes,
	useContextScope,
	useMessagePlacementHooks,
	useTrigger,
} from '@atlassian/post-office-context';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { createPlacementComponent } from '@post-office/placement-common/utils/create-placement-component';
import { MaybeSuspenseBoundary } from '@post-office/placement-common/utils/message-renderer';
import type { ErrorBoundariedProps } from '@post-office/placement-contexts/create-placement-component/types';
import { useInitialData } from '@post-office/placement-contexts/hooks';
import { usePlacementApi } from '@post-office/use-placement-api';
import {
	type ComponentType,
	type ReactNode,
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { scopes } from './scopes';
import { MessageRenderer } from '../__generated__/client/render';
import { PLACEMENT_ID } from '../__generated__/common';
import { withScreenSpaceFlagsIntlProvider } from '../__generated__/intl-provider';
import { type ScreenSpaceFlagsMessageState } from '../api/types';
import type { Placement as ScreenSpaceFlagsPlacement } from '../contracts/Placement';

export const matchedScopes = (input: {
	route: RouteState | RouteMatch;
	context: PostOfficeContextValue;
}): ActiveScopeEntry[] => createMatchScopes({ scopes })(input);

interface ExperimentalMessage {
	messageTemplateId: string;
	statsigExperiment: string;
	statsigParameterName: string;
	statsigTestCohort: string;
	statsigAAExperiment?: string;
}

// Entries are iterated through using the ML ranking order - the order in the list is not important
export const EXPERIMENTAL_MESSAGES: ExperimentalMessage[] = [
	{
		messageTemplateId: 'confluence-nudge-for-jira',
		statsigExperiment: 'post-office_expt_confluence-nudge-for-jira',
		statsigAAExperiment: 'post-office_expt_confluence-nudge-for-jira_aa',
		statsigParameterName: 'cohort',
		statsigTestCohort: 'variation',
	},
	{
		messageTemplateId: 'confluence-inline-reactions-mcu-nudge',
		statsigExperiment: 'cc-engagement-convert-dwell-only-mcu-to-react-exp',
		statsigAAExperiment: 'cc-engagement-convert-dwell-only-mcu-exp_aa',
		statsigParameterName: 'cohort',
		statsigTestCohort: 'experiment',
	},
];

export const ScreenSpaceFlagsPlacementComponent: ComponentType<
	ErrorBoundariedProps<ScreenSpaceFlagsPlacement>
> = withScreenSpaceFlagsIntlProvider(
	createPlacementComponent<ScreenSpaceFlagsPlacement>(
		PLACEMENT_ID,
		memo(
			({
				initialData,
				preloadedInitialData,
				setPreloadedInitialData,
				jiraFlagsService,
				reportExpectedError,
				reportUnexpectedError,
			}) => {
				const isEPExternalMessageSSFEnabled = FeatureGates.checkGate(
					'post-office_enable_ep_external_message_web',
				);

				const isEPExternalMessageSSREnabled = FeatureGates.checkGate(
					'post-office_enabled_ep_external_message_ssr',
				);

				const [localMessageState, setLocalMessageState] = useState<
					ScreenSpaceFlagsMessageState[] | undefined
				>(() => {
					if (preloadedInitialData) {
						return preloadedInitialData?.messages?.length > 0
							? preloadedInitialData?.messages
							: undefined;
					}

					return undefined;
				});

				const [localExternalMessageState, setLocalExternalMessageState] = useState<
					ScreenSpaceFlagsMessageState[] | undefined
				>(() => {
					// Initialize from preloadedInitialData only if both feature gates are enabled
					if (
						isEPExternalMessageSSREnabled &&
						isEPExternalMessageSSFEnabled &&
						preloadedInitialData?.externalMessages
					) {
						return preloadedInitialData.externalMessages.length > 0
							? preloadedInitialData.externalMessages
							: undefined;
					}
					return undefined;
				});
				useInitialData(initialData);

				const { onMessageDismiss, onMessageClicked, onMessageSnooze } = useMessagePlacementHooks();

				const handleDismiss = useCallback<DismissCallback>(
					({ messageInstanceId }: { messageInstanceId: string }) => {
						if (isEPExternalMessageSSREnabled && isEPExternalMessageSSFEnabled) {
							// NEW LOGIC: Improved dismissal handling with precise checking and state tracking
							let updatedLocalMessageState: ScreenSpaceFlagsMessageState[] | undefined;

							// Check if message is in localMessageState
							setLocalMessageState((current) => {
								if (current?.some((msg) => msg.messageInstanceId === messageInstanceId)) {
									const newState = current.filter((e) => e.messageInstanceId !== messageInstanceId);
									updatedLocalMessageState = newState;

									if (typeof setPreloadedInitialData === 'function') {
										setPreloadedInitialData({
											messages: newState,
											externalMessages: preloadedInitialData?.externalMessages,
										});
									}

									return newState;
								}

								return current;
							});

							// Check if message is in localExternalMessageState
							setLocalExternalMessageState((current) => {
								if (current?.some((msg) => msg.messageInstanceId === messageInstanceId)) {
									const newState = current.filter((e) => e.messageInstanceId !== messageInstanceId);
									const updatedExternalMessages = newState.length > 0 ? newState : undefined;

									if (typeof setPreloadedInitialData === 'function') {
										setPreloadedInitialData({
											messages: updatedLocalMessageState ?? preloadedInitialData?.messages ?? [],
											externalMessages: updatedExternalMessages,
										});
									}

									return updatedExternalMessages;
								}

								return current;
							});
						} else {
							// OLD LOGIC: Original behavior when feature gate is disabled
							setLocalMessageState((current) => {
								if (current) {
									const newState = current.filter((e) => e.messageInstanceId !== messageInstanceId);

									if (typeof setPreloadedInitialData === 'function') {
										setPreloadedInitialData({
											messages: newState,
										});
									}

									return newState;
								}

								return undefined;
							});
						}
					},
					[
						setPreloadedInitialData,
						preloadedInitialData,
						isEPExternalMessageSSFEnabled,
						isEPExternalMessageSSREnabled,
					],
				);

				const onDismissed = useCallback(() => {
					setLocalMessageState([]);

					if (isEPExternalMessageSSREnabled && isEPExternalMessageSSFEnabled) {
						// NEW LOGIC: Clear external message state and update preloadedInitialData
						setLocalExternalMessageState(undefined);

						if (typeof setPreloadedInitialData === 'function') {
							setPreloadedInitialData({
								messages: [],
								...(isEPExternalMessageSSFEnabled && {
									externalMessages: undefined,
								}),
							});
						}
					} else {
						// OLD LOGIC: Only clear regular messages
						if (typeof setPreloadedInitialData === 'function') {
							setPreloadedInitialData({
								messages: [],
							});
						}
					}
				}, [setPreloadedInitialData, isEPExternalMessageSSFEnabled, isEPExternalMessageSSREnabled]);

				useEffect(() => onMessageDismiss(handleDismiss), [onMessageDismiss]);
				useEffect(() => onMessageClicked(handleDismiss), [onMessageClicked]);
				useEffect(() => onMessageSnooze(handleDismiss), [onMessageSnooze]);

				const activeScopes = useContextScope({
					scopes,
					treatIntersectionAsSame: FeatureGates.checkGate(
						'post-office-ssf-treat-intersection-as-same',
					),
				});

				const messagesState = usePlacementApi<{
					messages: ScreenSpaceFlagsMessageState[];
					externalMessages?: ScreenSpaceFlagsMessageState[];
				}>('/update-messages', {
					queryParams: {
						updateKey: activeScopes.updateKey ?? '',
					},
					requestOptions: { method: 'POST', body: JSON.stringify(activeScopes) },
					throttleTime: 100,
					autoRefetch: {
						isEnabled: FeatureGates.checkGate('post-office-ssf-enable-continuous-auto-refetch')
							? !!activeScopes.updateKey && !localMessageState?.length
							: !!activeScopes.updateKey && typeof localMessageState === 'undefined',
						updateKey: activeScopes.updateKey ?? '',
					},
				});

				useEffect(() => {
					if (messagesState?.data?.messages?.length) {
						const messages = messagesState.data.messages;
						let selectedMessage: ScreenSpaceFlagsMessageState | undefined;

						// New experiments in the screen-space-flags placement must be added to the EXPERIMENTAL_MESSAGES list
						for (const message of messages) {
							const experimentalMessage = EXPERIMENTAL_MESSAGES.find(
								(p) => p.messageTemplateId === message.messageTemplateId,
							);

							if (experimentalMessage) {
								const cohort = FeatureGates.getExperimentValue<string>(
									experimentalMessage.statsigExperiment,
									experimentalMessage.statsigParameterName,
									'not-enrolled',
									{ fireExperimentExposure: true },
								);

								if (experimentalMessage?.statsigAAExperiment) {
									FeatureGates.getExperimentValue<string>(
										experimentalMessage.statsigAAExperiment,
										experimentalMessage.statsigParameterName,
										'not-enrolled',
										{ fireExperimentExposure: true },
									);
								}

								if (cohort === experimentalMessage.statsigTestCohort) {
									selectedMessage = message;
									break;
								}
							} else {
								selectedMessage = message;
								break;
							}
						}
						setLocalMessageState(selectedMessage ? [selectedMessage] : []);
					}

					if (isEPExternalMessageSSFEnabled && messagesState.data?.externalMessages?.length) {
						setLocalExternalMessageState(messagesState.data.externalMessages);
					}
				}, [messagesState.data]);

				const { triggerStates: epTriggerStates, EpTriggerWrapper } = useEpMigrationTriggers(
					isEPExternalMessageSSFEnabled ? localExternalMessageState : undefined,
				);

				// find if an EP message has been triggered
				const activeEpMessageInstanceId = isEPExternalMessageSSFEnabled
					? Object.keys(epTriggerStates).find(
							(messageInstanceId) => epTriggerStates[messageInstanceId] === true,
						)
					: undefined;

				// Check for EP External messages first because they are not rendered until triggered from the front-end
				// and they will not be rendered if a non-ep message is currently rendered due to the choreographer
				const [currentLocalMessage] = localMessageState ?? [];

				const currentExternalMessage = localExternalMessageState?.find(
					(msg) => msg.messageInstanceId === activeEpMessageInstanceId,
				);

				const activeExternalMessage =
					isEPExternalMessageSSFEnabled && activeEpMessageInstanceId
						? currentExternalMessage
						: currentLocalMessage;

				const messageToRender = currentLocalMessage ?? activeExternalMessage;

				const content = FeatureGates.checkGate('post-office-screen-space-flags-shadow') ? (
					<SuppressIfModalIsPresent>
						{messageToRender ? (
							<MessageRenderer
								{...messageToRender}
								key={messageToRender.messageInstanceId}
								id={messageToRender.messageInstanceId}
								messageInstanceId={messageToRender.messageInstanceId}
								onDismissed={onDismissed}
								SuspenseBoundary={MaybeSuspenseBoundary}
								jiraFlagsService={jiraFlagsService}
								onError={reportUnexpectedError}
								reportExpectedError={reportExpectedError}
							/>
						) : null}
					</SuppressIfModalIsPresent>
				) : null;

				// Wrap content with trigger tracker components if feature gate is enabled
				return isEPExternalMessageSSFEnabled ? (
					<EpTriggerWrapper>{content}</EpTriggerWrapper>
				) : (
					content
				);
			},
		),
		{
			choreographer: {
				isDefaultEnabled: true, // this is the default value, but leaving it as explicitly defined
			},
		},
	),
);

export default ScreenSpaceFlagsPlacementComponent;

const SuppressIfModalIsPresent = ({ children }: React.PropsWithChildren<unknown>): JSX.Element => {
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const [isVisible, setIsVisible] = useState(true);

	const handleVisibilityChange = useCallback(
		(modalOnScreen: boolean) => {
			const shouldShow = !modalOnScreen;

			if (!shouldShow) {
				fireAnalyticsEvent({
					eventType: 'operational',
					action: 'suppressed',
					actionSubject: 'screenSpaceFlags',
				});
			}

			setIsVisible(shouldShow);
		},
		[fireAnalyticsEvent],
	);

	useEffect(() => {
		const modalOnScreen = !!globalThis.document.querySelector(
			'.atlaskit-portal-container .atlaskit-portal [role=dialog]',
		);

		handleVisibilityChange(modalOnScreen);
	}, [handleVisibilityChange]);

	return FeatureGates.checkGate('post-office-screen-space-flags-modal-detector') ? (
		<>{isVisible && children}</>
	) : (
		<>{children}</>
	);
};

// Component to track individual trigger states
// Each instance calls useTrigger independently, avoiding rules of hooks violations
const EpTriggerTracker = ({
	messageInstanceId,
	epMessageId,
	onTriggerChange,
}: {
	messageInstanceId: string;
	epMessageId: string;
	onTriggerChange: (messageInstanceId: string, isActive: boolean) => void;
}) => {
	const { isActive: requestStart } = useTrigger(epMessageId + '-start');
	const { isActive: isStarted } = useTrigger(epMessageId + '-started');
	const { isActive: requestDismiss } = useTrigger(epMessageId + '-dismiss');
	const { isActive: ctaClick } = useTrigger(epMessageId + '-cta-click');
	const isActiveValue = requestStart || isStarted || requestDismiss || ctaClick || false;

	useEffect(() => {
		onTriggerChange(messageInstanceId, isActiveValue);
	}, [messageInstanceId, isActiveValue, onTriggerChange]);

	return null; // This component doesn't render anything
};

// This hook is used to track the state of EP External Message Triggers
// It returns an object with the message instance id as the key and the trigger state as the value
// The trigger state is true if the trigger is active, false otherwise
// The trigger state will only change to true when the trigger is set to active from the product front-end
const useEpMigrationTriggers = (messages: ScreenSpaceFlagsMessageState[] | undefined) => {
	const [triggerStates, setTriggerStates] = useState<Record<string, boolean>>({});

	// Get all ep-migration messages
	const epMessages = useMemo(
		() =>
			messages?.filter(
				(message) => message.messageTemplateId === 'ep-migration-external-messages',
			) || [],
		[messages],
	);

	const handleTriggerChange = useCallback((messageInstanceId: string, isActive: boolean) => {
		setTriggerStates((prev) => {
			// Only update if the value actually changed
			if (prev[messageInstanceId] === isActive) {
				return prev;
			}
			return {
				...prev,
				[messageInstanceId]: isActive,
			};
		});
	}, []);

	const EpTriggerWrapper = useMemo(
		() =>
			({ children }: { children: ReactNode }) => (
				<>
					{epMessages.map((message: ScreenSpaceFlagsMessageState & { epMessageId: string }) => {
						const epMessageId = message.epMessageId;
						return epMessageId && typeof epMessageId === 'string' ? (
							<EpTriggerTracker
								key={message.messageInstanceId}
								messageInstanceId={message.messageInstanceId}
								epMessageId={epMessageId}
								onTriggerChange={handleTriggerChange}
							/>
						) : null;
					})}
					{children}
				</>
			),
		[epMessages, handleTriggerChange],
	);

	return {
		triggerStates,
		EpTriggerWrapper,
	};
};
